// OfferCard — one offer rendered as a card. Used by the Rewards page
// in all three tabs (Available / In Review / Claimed); the card adapts
// its action surface based on the claim state passed in.
//
// Layout:
//   ┌───────────────────────────────────────────────┐
//   │ [icon]  Title                  ₹50  / 10%      │
//   │         Description tag-line                   │
//   │         expires Apr 29 · per-user limit 1      │
//   │ ─────────────────────────────────────────────  │
//   │ [primary action]   [secondary action]          │
//   └───────────────────────────────────────────────┘
//
// The primary action depends on (offer.approval, latest claim state):
//   - auto  + no claim     → "Claim" button (calls POST /me/offers/:id/claim)
//   - auto  + credited     → "Claimed ✓ on <date>" badge
//   - manual + no claim    → "Submit for review" button (same endpoint)
//   - manual + pending     → "In review" badge (amber)
//   - any    + rejected    → "Rejected — try again" (re-submit available)

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import Icon from './Icon';
import { useT } from '../lib/i18n';

export type OfferCta = {
  /** 'claim' = render Claim button (engine fires on click).
   *  'navigate' = render a Link button to `target` so the player can
   *  do whatever earns the offer (recharge, play a game). The credit
   *  fires automatically once the player meets the criteria. */
  mode: 'claim' | 'navigate';
  label: string;
  target?: string;
};

export type OfferLike = {
  id: number;
  title: string;
  description: string | null;
  /** Phase 14: optional artwork URL (640x640 PNG with transparency).
   *  When present, the card renders the 3D pop-out variant; otherwise
   *  it falls back to the icon-on-left layout (visually unchanged from
   *  the pre-Phase-14 design). */
  image_url?: string | null;
  offer_type: string;
  reward_kind: 'cash' | 'percent' | 'item';
  reward_value: number;
  reward_cap: number | null;
  approval: 'auto' | 'manual';
  starts_at: string | null;
  ends_at: string | null;
  per_user_limit: number;
  global_budget: number | null;
  budget_used: number;
  my_latest_claim: ClaimLike | null;
  /** Phase 16.12 — backend-built CTA descriptor. Tells the card
   *  whether to render the Claim button (player-initiated trigger)
   *  or a navigate-to-action Link (recharge / play). Optional for
   *  backwards compatibility with older list responses. */
  cta?: OfferCta;
};

export type ClaimLike = {
  id: number;
  state: 'pending' | 'approved' | 'rejected' | 'credited' | 'expired';
  computed_amount: number;
  decision_note?: string | null;
  created_at: string;
};

// Material Symbol per offer_type. Falls back to "redeem" for any
// unrecognised type so adding a new offer_type doesn't break rendering.
const TYPE_ICON: Record<string, string> = {
  signup_bonus:       'celebration',
  daily_checkin:      'calendar_month',
  admin_announcement: 'campaign',
  recharge_bonus:     'savings',
  play_streak:        'whatshot',
  game_challenge:     'sports_esports',
  win_streak:         'military_tech',
  time_played:        'schedule',
  wallet_spend:       'payments',
  cashback:           'restart_alt',
};

// Per-type accent (border + glow) — keeps the card visually distinct
// at a glance even before the title is read.
const TYPE_ACCENT: Record<string, string> = {
  signup_bonus:       'from-pink-500/20 to-rose-500/10 border-rose-400/30',
  daily_checkin:      'from-emerald-500/20 to-green-500/10 border-emerald-400/30',
  admin_announcement: 'from-cyan-500/20 to-sky-500/10 border-cyan-400/30',
  recharge_bonus:     'from-amber-500/20 to-orange-500/10 border-amber-400/30',
};

export default function OfferCard({
  offer,
  onClaim,
  busy = false,
}: {
  offer: OfferLike;
  onClaim?: (offerId: number) => void | Promise<void>;
  busy?: boolean;
}) {
  const t = useT();
  const claim = offer.my_latest_claim;
  const accent = TYPE_ACCENT[offer.offer_type] ?? 'from-neon-400/15 to-brand-400/10 border-neon-400/25';

  // Reward formatted for display.
  const rewardText = offer.reward_kind === 'percent'
    ? `${offer.reward_value}%${offer.reward_cap ? ` (${t('rewards.cap')} ₹${offer.reward_cap.toLocaleString('en-IN')})` : ''}`
    : `₹${offer.reward_value.toLocaleString('en-IN')}`;

  // Lifecycle line — show whichever bits are set.
  const lifecycleBits: string[] = [];
  if (offer.ends_at) {
    const d = new Date(offer.ends_at);
    lifecycleBits.push(t('rewards.expires', { date: d.toLocaleDateString() }));
  }
  if (offer.global_budget != null) {
    const remaining = Math.max(0, offer.global_budget - (offer.budget_used || 0));
    lifecycleBits.push(t('rewards.remaining', { amount: '₹' + remaining.toLocaleString('en-IN') }));
  }

  // Phase 14: when artwork is present, render the 3D pop-out variant
  // (image floats top-right, breaks out of the card edge, with a halo
  // glow behind it). When NULL, render the original icon-on-left
  // layout exactly as it shipped pre-Phase-14 — preserving the look
  // the user explicitly called out as "already very beautiful".
  if (offer.image_url) {
    return (
      <div
        className={`relative rounded-2xl border bg-gradient-to-br ${accent} backdrop-blur p-4 space-y-3
                    overflow-visible
                    shadow-[0_8px_24px_-12px_rgba(0,0,0,0.35)]
                    transition-transform duration-200 hover:-translate-y-0.5`}
      >
        {/* Artwork tile — absolutely positioned in the top-right corner,
            breaking out of the card boundary by ~14px (above) and ~6px
            (right) for the 3D pop-out / diorama effect. The cyan/orange
            halos that used to sit behind the image were removed in
            Phase 16.5 — they read as fake light and distracted from the
            artwork. Now we use a single small black drop-shadow on the
            <img> itself: 3px down, 4px blur, 20% opacity. Reads as a
            soft surface contact shadow with light coming from the top.
            Sizes stay responsive: 80px on phones, 112px on sm+. */}
        <div
          className="pointer-events-none absolute -top-3.5 -right-1.5
                     w-20 h-20 sm:w-28 sm:h-28 z-10"
          aria-hidden="true"
        >
          <img
            src={offer.image_url}
            alt=""
            loading="lazy"
            decoding="async"
            className="relative w-full h-full object-contain
                       drop-shadow-[0_3px_4px_rgba(0,0,0,0.20)]"
          />
        </div>

        {/* Text content — pad the right side to clear the artwork tile.
            pr-20 (80px) on mobile matches the 80px image; pr-28 (112px)
            on sm+ matches the 112px image. line-clamp keeps long text
            from wrapping under the image. */}
        <div className="pr-20 sm:pr-28 space-y-1">
          <h3 className="font-display font-extrabold text-base leading-tight
                         text-arena-800 dark:text-neon-100 line-clamp-2">
            {offer.title}
          </h3>
          <div className="font-display font-black text-lg sm:text-xl tabular-nums
                          text-brand-600 dark:text-brand-400 leading-none">
            {rewardText}
          </div>
          {offer.description && (
            <p className="text-xs text-arena-600 dark:text-neon-200/70 line-clamp-2 pt-0.5">
              {offer.description}
            </p>
          )}
          {lifecycleBits.length > 0 && (
            <div className="text-[10px] uppercase tracking-wider text-arena-500
                            dark:text-neon-200/50 truncate pt-0.5">
              {lifecycleBits.join(' · ')}
            </div>
          )}
        </div>

        {/* Action / state line */}
        <div className="pt-2 border-t border-white/10 dark:border-white/5">
          <ActionLine offer={offer} claim={claim} onClaim={onClaim} busy={busy} t={t} />
        </div>
      </div>
    );
  }

  // No artwork — original layout, unchanged.
  return (
    <div className={`rounded-2xl border bg-gradient-to-br ${accent} backdrop-blur p-4 space-y-3`}>
      <div className="flex items-start gap-3">
        {/* Type icon */}
        <div className="shrink-0 w-11 h-11 rounded-xl grid place-items-center
                        bg-white/10 dark:bg-white/5 border border-white/10
                        text-arena-700 dark:text-neon-200">
          <Icon name={TYPE_ICON[offer.offer_type] ?? 'redeem'} size={22} filled />
        </div>

        {/* Title + description */}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <h3 className="font-display font-extrabold text-base leading-tight text-arena-800 dark:text-neon-100 truncate">
              {offer.title}
            </h3>
            {/* Reward amount, big & bold */}
            <span className="font-display font-black text-lg tabular-nums text-brand-600 dark:text-brand-400 shrink-0">
              {rewardText}
            </span>
          </div>
          {offer.description && (
            <p className="text-xs text-arena-600 dark:text-neon-200/70 mt-0.5 line-clamp-2">
              {offer.description}
            </p>
          )}
          {lifecycleBits.length > 0 && (
            <div className="text-[10px] uppercase tracking-wider text-arena-500 dark:text-neon-200/50 mt-1.5 truncate">
              {lifecycleBits.join(' · ')}
            </div>
          )}
        </div>
      </div>

      {/* Action / state line */}
      <div className="pt-1 border-t border-white/10 dark:border-white/5">
        <ActionLine offer={offer} claim={claim} onClaim={onClaim} busy={busy} t={t} />
      </div>
    </div>
  );
}

function ActionLine({
  offer, claim, onClaim, busy, t,
}: {
  offer: OfferLike;
  claim: ClaimLike | null;
  onClaim?: (id: number) => void | Promise<void>;
  busy: boolean;
  t: ReturnType<typeof useT>;
}) {
  // Already credited — congratulatory badge with date.
  if (claim?.state === 'credited') {
    return (
      <Badge tone="success" icon="check_circle">
        {t('rewards.claimed.credited', {
          amount: '₹' + claim.computed_amount.toLocaleString('en-IN'),
          date: new Date(claim.created_at).toLocaleDateString(),
        })}
      </Badge>
    );
  }

  // Manual offer with pending review.
  if (claim?.state === 'pending' || claim?.state === 'approved') {
    return <Badge tone="warning" icon="pending">{t('rewards.claimed.pending')}</Badge>;
  }

  // Rejected — show why, NO retry button. The engine's UNIQUE index
  // (offer_id, user_id, trigger_kind, trigger_ref) means a re-claim
  // would just hit INSERT IGNORE and silently bounce as 'duplicate'.
  // Phase 16.11: replaced the misleading "try again" Claim button
  // with a plain rejected badge so the user isn't stuck clicking a
  // button that does nothing.
  if (claim?.state === 'rejected') {
    return (
      <Badge tone="error" icon="cancel">
        {claim.decision_note ? `"${claim.decision_note}"` : t('rewards.claimed.rejected')}
      </Badge>
    );
  }

  // No claim yet — Phase 16.12: render the contextual CTA based on
  // offer.cta. For pure-claim types (signup_bonus / daily_checkin /
  // admin_announcement) cta.mode === 'claim' and we render the Claim
  // button as before. For all other types cta.mode === 'navigate'
  // and we render a Link button to the screen where the player earns
  // the offer (recharge page for recharge_bonus, the relevant game
  // page for play-based offers, the games list for cross-platform
  // offers like wallet_spend / time_played / play_streak).
  //
  // The credit fires automatically once the player meets criteria.
  //
  // Backwards compatibility: if cta is missing (older list response),
  // fall back to the legacy "always show Claim button" behavior.
  const cta = offer.cta;
  return (
    <div className="flex items-center justify-end">
      {!onClaim ? (
        <span className="text-[11px] uppercase tracking-wider text-arena-500 dark:text-neon-200/50">
          {offer.approval === 'auto' ? t('rewards.auto.willCredit') : t('rewards.auto.willReview')}
        </span>
      ) : cta && cta.mode === 'navigate' && cta.target ? (
        <NavCtaButton label={cta.label} to={cta.target} />
      ) : (
        <ClaimButton offerId={offer.id} approval={offer.approval} onClaim={onClaim} busy={busy} t={t} />
      )}
    </div>
  );
}

// Phase 16.12 — non-claim contextual CTA. Visually distinct from the
// brand-orange Claim button (cyan electric, leaning forward) so the
// player understands "this takes me somewhere" vs "this fires now."
function NavCtaButton({ label, to }: { label: string; to: string }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full
                 font-bold text-[12px] tracking-wider uppercase text-white
                 bg-gradient-to-b from-cyan-500 via-cyan-600 to-cyan-700
                 border border-white/15 hover:from-cyan-400 hover:via-cyan-500
                 active:scale-[0.98] transition
                 shadow-[0_2px_10px_rgba(6,182,212,0.35)]"
    >
      <span>{label}</span>
      <Icon name="arrow_forward" size={14} />
    </Link>
  );
}

function ClaimButton({
  offerId, approval, onClaim, busy, t,
}: {
  offerId: number;
  approval: 'auto'|'manual';
  onClaim: (id: number) => void | Promise<void>;
  busy: boolean;
  t: ReturnType<typeof useT>;
}) {
  return (
    <button
      type="button"
      disabled={busy}
      onClick={() => onClaim(offerId)}
      className="inline-flex items-center gap-1 h-9 px-4 rounded-full
                 font-bold text-[12px] tracking-wider uppercase text-white
                 bg-gradient-to-b from-brand-400 via-brand-500 to-brand-600
                 border border-white/15 active:scale-[0.98] transition disabled:opacity-50"
      style={{ boxShadow: '0 6px 16px -8px rgba(var(--brand-rgb),0.7), inset 0 1px 0 rgba(255,255,255,0.3)' }}
    >
      {busy ? (
        <><Icon name="progress_activity" size={14} className="animate-spin" /> {t('common.loading')}</>
      ) : approval === 'auto' ? (
        <>{t('rewards.claim.cta')} <Icon name="arrow_forward" size={14} filled /></>
      ) : (
        <>{t('rewards.claim.submit')} <Icon name="arrow_forward" size={14} filled /></>
      )}
    </button>
  );
}

function Badge({ tone, icon, children }: { tone: 'success'|'warning'|'error'; icon: string; children: ReactNode }) {
  // Phase 16.11: added 'error' tone for the rejected-claim badge.
  const map = {
    success: 'bg-emerald-100/70 text-emerald-800 border-emerald-300 dark:bg-emerald-400/15 dark:text-emerald-200 dark:border-emerald-400/30',
    warning: 'bg-amber-100/70 text-amber-800 border-amber-300 dark:bg-amber-400/10 dark:text-amber-200 dark:border-amber-400/30',
    error:   'bg-rose-100/70 text-rose-800 border-rose-300 dark:bg-rose-400/15 dark:text-rose-200 dark:border-rose-400/30',
  } as const;
  return (
    <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-bold ${map[tone]}`}>
      <Icon name={icon} size={14} filled />
      {children}
    </div>
  );
}
